/**
 * Onde está o servidor.
 *
 * Na web é a própria página: `location.origin`, e tudo funciona com caminhos
 * relativos. Numa app Tauri a página vem do protocolo de assets do próprio
 * webview (`tauri://localhost`, ou `http://tauri.localhost` no Windows),
 * logo `/api/v1` apontaria para dentro do bundle e nunca sairia da máquina.
 *
 * Um só sítio a resolver isto, porque a alternativa é descobri-lo três vezes
 * — REST, gateway, sinalização de media — e esquecer uma.
 *
 * Ordem de precedência:
 *   1. `window.__TASCA_ORIGIN__`, injectado pelo shell nativo em runtime.
 *      Runtime e não build: o mesmo instalador tem de poder apontar para
 *      outra instância sem ser recompilado.
 *   2. `VITE_TASCA_ORIGIN`, para um `pnpm dev` a falar com um servidor remoto.
 *   3. `location.origin` — o caso da web, e o único em que não há nada a
 *      configurar.
 */

declare global {
  interface Window {
    __TASCA_ORIGIN__?: string;
  }
}

function resolve(): string {
  const injected = typeof window !== 'undefined' ? window.__TASCA_ORIGIN__ : undefined;
  const fromEnv = import.meta.env['VITE_TASCA_ORIGIN'] as string | undefined;
  const origin = injected ?? fromEnv ?? location.origin;
  return origin.replace(/\/$/, '');
}

/**
 * Resolvido uma vez, no arranque.
 *
 * Mudar de servidor a meio da sessão deixaria sockets abertos contra o
 * anterior e estado meio migrado; a forma suportada de o fazer é reiniciar a
 * app, e o shell nativo injecta o valor antes de a página correr.
 */
export const ORIGIN = resolve();

/** `https://host` → `wss://host`. */
export const WS_ORIGIN = ORIGIN.replace(/^http/, 'ws');

/** Verdadeiro quando a página não é servida pelo próprio servidor da API. */
export const CROSS_ORIGIN =
  typeof location !== 'undefined' && ORIGIN !== location.origin.replace(/\/$/, '');
